import type { ReactNode } from "react";

import {
  KaiCustomerRequestCard,
  parseCustomerRequest,
} from "./KaiCustomerRequestCard";
import { KaiRateCardDiffCard, parseRateCardDiff } from "./KaiRateCardDiffCard";
import {
  KaiRestrictionProposalCard,
  parseRestrictionProposal,
} from "./KaiRestrictionProposalCard";

/**
 * Registry of structured preview components for MCP elicitations. An
 * elicitation schema may carry a `ui_component` tag plus a `preview` payload;
 * this registry maps the tag to a parser + renderer so `KaiElicitationCard`
 * resolves rich previews without an inline `if (ui_component === …)` chain.
 */
export interface KaiElicitationComponentDef<T = unknown> {
  uiComponent: string;
  /** Validate the untyped `preview` payload; return `null` on shape mismatch. */
  parse: (preview: unknown) => T | null;
  render: (spec: T) => ReactNode;
}

const REGISTRY: Record<string, KaiElicitationComponentDef<unknown>> = {};

function register<T>(def: KaiElicitationComponentDef<T>): void {
  REGISTRY[def.uiComponent] = def as KaiElicitationComponentDef<unknown>;
}

register({
  uiComponent: "rate_card_diff",
  parse: parseRateCardDiff,
  render: (spec) => <KaiRateCardDiffCard diff={spec} />,
});

register({
  uiComponent: "restriction_proposal",
  parse: parseRestrictionProposal,
  render: (spec) => <KaiRestrictionProposalCard spec={spec} />,
});

register({
  uiComponent: "customer_request",
  parse: parseCustomerRequest,
  render: (spec) => <KaiCustomerRequestCard spec={spec} />,
});

/**
 * Render the structured preview for an elicitation, or `null` when the schema
 * has no `ui_component`, the tag is unregistered, or the payload fails to
 * parse (so the caller can fall back to its text preview).
 */
export function renderElicitationPreview(
  uiComponent: string | undefined,
  preview: unknown,
): ReactNode | null {
  if (!uiComponent) return null;
  const def = REGISTRY[uiComponent];
  if (!def) return null;
  const spec = def.parse(preview);
  return spec != null ? def.render(spec) : null;
}
